import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import React, { useState } from "react";
import Dropzone from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreButtonIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import { getUploadURL, uploadFile } from "./CaricaDocumentiSlice";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDoCheckDuplicateMutation,
} from "../../api/user/gestisciDocumentiApi";
import { t } from "i18next";
import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import CoreTextArea from "../../../app/core/components/CoreTextArea/CoreTextArea";
import LockScreen from "../../../app/core/components/CoreLockScreen/LockScreen";
import CoreBack from "../../../app/core/components/CoreBack/CoreBack";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import CoreModal from "../../../app/core/components/CoreModal/CoreModal";
import CoreDuplicate from "../../../app/core/components/CoreDuplicate.jsx/CoreDuplicate";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const CaricaDocumentiPage = () => {
  const dispatch = useDispatch();
  const primaryColor = useSelector((state) => state.app.primaryColor);
  const userId = useSelector((state) => state.app.adminId);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileIndexForEditing, setFileIndexForEditing] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [isUploadEnded, setIsUploadEnded] = useState(false);
  const [isAllDocumentsUploaded, setIsAllDocumentsUploaded] = useState(false);
  const [failedUploads, setFailedUploads] = useState([]);
  const [duplicateToHandle, setDuplicateToHandle] = useState(null);

  const [createDocument] = useCreateDocumentMutation();
  const [checkDuplicate] = useDoCheckDuplicateMutation();
  const [deleteDocument] = useDeleteDocumentMutation();

  const clear = () => {
    setIsUploadEnded(false);
    setIsAllDocumentsUploaded(false);
    setSelectedFiles([]);
    setProgress(0);
  };

  const handleFileDrop = (acceptedFiles) => {
    if (isUploadEnded) clear();

    const validFiles = acceptedFiles.filter(
      (file) =>
        file.type === "application/pdf" || file.type.startsWith("image/")
    );

    if (validFiles.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    } else {
      toast.error(t("InvalidFile"));
    }
  };

  const rimuoviFile = (index) => {
    const nuoviFiles = [...selectedFiles];
    nuoviFiles.splice(index, 1);
    setSelectedFiles(nuoviFiles);
  };

  const createDocumentData = (files) => {
    const firstFile = files[0];
    const fileExtension = firstFile.name.split(".").pop();
    const documents = files.map((file) => ({
      extension: file.name.split(".").pop(),
      fileName: file.name,
    }));
    const documentData = {
      ownerId: userId,
      filesCount: files.length,
      name: firstFile.name,
      fileName: firstFile.name,
      extensions: [fileExtension],
      documentScope: "PERSONAL",
      notes: firstFile.notes,
      documents: documents,
    };
    return documentData;
  };

  const updateProgress = (percentage) => {
    setProgress(percentage);
  };

  const confermaCaricamento = async () => {
    if (selectedFiles.length === 0) return;
    setLoading(true);
    setIsUploadEnded(true);
    setFailedUploads([]);
    const totalFiles = selectedFiles.length;
    let uploadedFilesCount = 0;

    try {
      for (const file of selectedFiles) {
        try {
          const uploadUrlResponse = await dispatch(getUploadURL());
          const uploadUrl = uploadUrlResponse.payload;
          await dispatch(
            uploadFile({
              url: uploadUrl,
              file: new Blob([file]),
              type: file.type,
            })
          );
          const documentData = createDocumentData([file]);
          const cleanedUrl = [uploadUrl.split("?")[0]];
          const cleanedDocumentData = {
            ...documentData,
            s3FilesPath: cleanedUrl,
          };

          let response;

          const checkBody = {
            documentName: cleanedDocumentData.name,
            ownerId: cleanedDocumentData.ownerId,
          };

          const responseCheck = await checkDuplicate(checkBody);

          if (responseCheck.data.Data) {
            toast.error(t("DuplicateDocument"), {
              autoClose: 1000,
            });
            setFailedUploads((prev) => [
              ...prev,
              {
                file,
                reason: "duplicate",
                docId: responseCheck.data.Data,
                payload: cleanedDocumentData,
              },
            ]);
            continue;
          } else {
            response = await createDocument(cleanedDocumentData);
          }

          if (response?.data?.Result !== 1) {
            toast.error(t("DocsNotLoaded"));
            setFailedUploads((prev) => [
              ...prev,
              { file, reason: "upload_error" },
            ]);
            continue;
          }

          uploadedFilesCount++;
          const currentProgress = Math.round(
            (uploadedFilesCount / totalFiles) * 100
          );
          updateProgress(currentProgress);
        } catch (error) {
          console.error(error);
          setFailedUploads((prev) => [...prev, { file, reason: "exception" }]);
          continue;
        }
      }
      setIsAllDocumentsUploaded(true);
      toast.success(t("DocsLoaded"));
    } catch (error) {
      console.error(error);
      toast.error(t("DocsNotLoaded"));
    } finally {
      setLoading(false);
    }
  };

  const handleFileIndexForEditingChange = (i) => {
    if (i === fileIndexForEditing) {
      setFileIndexForEditing(-1);
      return;
    }
    setFileIndexForEditing(i);
  };

  const handleEditFile = (notes) => {
    const nuoviFiles = [...selectedFiles];
    nuoviFiles[fileIndexForEditing].notes = notes;
    setSelectedFiles(nuoviFiles);
  };

  const getFailedData = (targetFile) => {
    return failedUploads.find(({ file }) => file.name === targetFile.name);
  };

  const handleDuplicateSuccess = (newId, newFileData) => {
    const { name, fileName, files } = newFileData;

    setSelectedFiles((prev) =>
      prev.map((f) =>
        f.path === fileName || f.name === name
          ? {
              ...f,
              id: newId,
              files,
              name,
              status: "resolved",
            }
          : f
      )
    );
    setFailedUploads((prev) =>
      prev.filter((f) => f.file.path !== fileName && f.file.name !== name)
    );
  };

  return (
    <div className="w-11/12">
      <LockScreen
        text={
          <div className="w-full text-center mt-4 flex flex-col items-center gap-4">
            <h3 className="mb-1">{t("UploadingDocExt")}</h3>
            <p className="mt-0" style={{ color: primaryColor }}>
              {t("UploadingDocDisclaimer")}
            </p>
            <div className="w-96">
              <div>{progress + "%"}</div>

              <DotLottieReact
                src="https://lottie.host/640307c9-8f67-4933-a20b-5ccca2473aac/D56P0e8La1.json"
                loop
                autoplay
              />
            </div>
          </div>
        }
        openTrigger={loading}
        zIndex={10000}
      />
      <div className="flex  items-center w-full ">
        <CoreBack />
        <h2>{t("UploadDoc")}</h2>
      </div>
      <Box component={Paper} elevation={4} style={{ height: "75vh" }}>
        <div className="flex p-8 space-x-8">
          <div className="w-5/12">
            <div>
              <Dropzone onDrop={handleFileDrop} accept={["image/*", ".pdf"]}>
                {({ getRootProps, getInputProps }) => (
                  <div
                    className="flex justify-center items-center cursor-pointer"
                    {...getRootProps()}
                    style={{
                      border: "2px dashed #ccc",
                      minHeight: "65vh",
                    }}
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center justify-center">
                      <p className="text-center text-gray">{t("DropFile")}</p>
                      <CoreButtonIcon
                        icon={
                          <UploadFileRoundedIcon
                            className="text-gray"
                            sx={{ fontSize: 70 }}
                          />
                        }
                      />
                      <CoreButtonIcon
                        icon={<SearchRoundedIcon className="text-gray" />}
                        testo={t("Search")}
                        className="text-gray"
                      />
                    </div>
                  </div>
                )}
              </Dropzone>
            </div>
          </div>
          <div className="w-6/12">
            {selectedFiles.length > 0 ? (
              <div
                style={{ height: "69vh" }}
                className="flex flex-col items-center"
              >
                <h3 className="mx-4">
                  {isUploadEnded ? t("DocsLoaded") : t("DocsToLoad")}
                </h3>
                {!loading && (
                  <>
                    <div className="overflow-y-scroll w-full p-2 border-solid border border-stone-100 rounded-md">
                      {selectedFiles.map((file, index) => (
                        <>
                          <div
                            key={index}
                            className="flex items-center space-x-1"
                          >
                            <div className="flex items-center space-x-2 w-full">
                              <p>{file.name}</p>
                              {isUploadEnded &&
                                (() => {
                                  const failedData = getFailedData(file);
                                  if (failedData) {
                                    return (
                                      <div className="flex items-center space-x-2 text-red-600">
                                        <HighlightOffRoundedIcon />
                                        <span className="text-sm italic">
                                          {failedData.reason === "duplicate"
                                            ? t("Duplicate")
                                            : t("UploadError")}
                                        </span>
                                        {failedData.reason === "duplicate" && (
                                          <CoreButton
                                            className="btn-small"
                                            testo={t("Actions")}
                                            onClick={() =>
                                              setDuplicateToHandle({
                                                docId: failedData.docId,
                                                payload: failedData.payload,
                                              })
                                            }
                                          />
                                        )}
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div className="flex space-x-2 text-green-700">
                                        <CheckCircleIcon />
                                      </div>
                                    );
                                  }
                                })()}
                            </div>
                            <div className="w-full flex justify-end gap-1">
                              <Tooltip
                                title={
                                  selectedFiles[index]?.notes?.trim()
                                    ? t("Edit") + " " + t("Notes").toLowerCase()
                                    : t("Add") + " " + t("Notes").toLowerCase()
                                }
                              >
                                {!isUploadEnded && (
                                  <IconButton>
                                    <TextSnippetRoundedIcon
                                      onClick={() =>
                                        handleFileIndexForEditingChange(index)
                                      }
                                      className={
                                        selectedFiles[index]?.notes?.trim() &&
                                        "text-green-700"
                                      }
                                    />
                                  </IconButton>
                                )}
                              </Tooltip>
                              <IconButton>
                                <DeleteRoundedIcon
                                  onClick={() => rimuoviFile(index)}
                                  className="text-rose-700"
                                />
                              </IconButton>
                            </div>
                          </div>
                          {fileIndexForEditing === index && (
                            <CoreTextArea
                              className="w-3/4 mb-8"
                              rows={2}
                              name="notes"
                              label={t("Notes")}
                              onChange={(e) => handleEditFile(e.target.value)}
                              value={selectedFiles[index].notes}
                            />
                          )}
                        </>
                      ))}
                    </div>
                    {isAllDocumentsUploaded && (
                      <div className="flex flex-row mt-10 w-full gap-8 justify-center">
                        <CoreButton
                          className="btn-round"
                          onClick={() => clear()}
                          testo={t("NewUpload")}
                        />
                      </div>
                    )}
                    {!isUploadEnded && (
                      <div className="text-center mt-4 py-4 space-x-8">
                        <CoreButton
                          className="btn-round"
                          onClick={() => clear()}
                          testo="Reset"
                        />

                        <CoreButton
                          className="btn-round"
                          onClick={confermaCaricamento}
                          testo={t("Upload")}
                        />
                      </div>
                    )}
                    {/* <div className="mt-4">
                      <CoreButton
                        className={"btn-round"}
                        onClick={confermaCaricamento}
                        testo={t("Upload")}
                      />
                    </div> */}
                  </>
                )}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-gray">
                {t("NoDocs")}
              </div>
            )}
          </div>
        </div>
      </Box>
      <CoreModal
        width={"80%"}
        height={"95%"}
        onClose={() => setDuplicateToHandle(null)}
        state={!!duplicateToHandle}
        component={
          <CoreDuplicate
            createDocument={createDocument}
            deleteDocument={deleteDocument}
            docId={duplicateToHandle?.docId}
            payload={duplicateToHandle?.payload}
            onClose={() => setDuplicateToHandle(null)}
            onSuccess={handleDuplicateSuccess}
          />
        }
      />
    </div>
  );
};

export default CaricaDocumentiPage;
